import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import FloatingChatButton from './components/FloatingChatButton';
import ChatPopup from './components/ChatPopup';
import AccentDetector from './components/AccentDetector';
import './App.css';
import '@fortawesome/fontawesome-free/js/all.js'
import Csl from './components/Carousel'
import Txb from './components/TextBlock'
import BlockHeader from './components/BlockHeader'
import Estonia_H from './photos/Estonia_H'
import Estonia_L from './photos/Estonia_L'
import Poland_H from './photos/Poland_H'
import Poland_L from './photos/Poland_L'
import France_H from './photos/France_H'
import France_L from './photos/France_L'
import UAE_H from './photos/UAE_H'
import UAE_L from './photos/UAE_L'
import Greece_H from './photos/Greece_H'
import Greece_L from './photos/Greece_L'
import India_H from './photos/India_H'
import India_L from './photos/India_L'
import Spain_H from './photos/Spain_H'
import Spain_L from './photos/Spain_L'
import SoImg3_H from './assets/photos/Misc_H/so.jpg';
import SoImg3_L from './assets/photos/Misc_L/so.jpg';
// import EF_SET_H from './assets/photos/Misc_H/EF_SET_Certificate.jpg';
// import EF_SET_L from './assets/photos/Misc_L/EF_SET_Certificate.jpg';
import EnglishScore from './assets/pdf/certificates/EnglishScore_Certificate.pdf';
import SeavusImg_H from './assets/photos/Misc_H/Seavus_Certificate.jpg';
import SeavusImg_L from './assets/photos/Misc_L/Seavus_Certificate.jpg';
import Header_H from './photos/Header_H';
import Header_L from './photos/Header_L';
import RollingText from './components/RollingText';
import AnimatedHoopWithImage from './components/AnimatedHoopWithImage';
import AnimatedDownArrow from './components/AnimatedDownArrow';
import ButtonWithArrow from './components/ButtonWithArrow';
import ProgressiveImage from './components/ProgressiveImage';
import VideoPlayer from './components/VideoPlayer';
import KiteVideo from './assets/kitesurfing.mp4';
import PreviewImg from './assets/preview.jpg';
import MainVideo from './assets/video.mp4';
import ContactDrawer from './components/ContactDrawer';
import { TechRow } from './components/SequentialFlippingController';
import TypingEffectTextWrapper from './components/TypingEffectTextWrapper';
import AnimatedScrollMultilineText from './components/AnimatedScrollMultilineText';
import { ResponsiveElements } from './components/ResponsiveElements';
import AssistantChatRow from './components/AssistantChatRow';
import AppLayout from './components/AppLayout';
import VideoGenerationPage from './components/VideoGenerationPage';
import PdfPreview from './components/PdfPreview';
import HireRight from './assets/pdf/certificates/HireRight_Certificate.pdf';
import Luxoft from './assets/pdf/certificates/Luxoft_Certificate.pdf';
import { useClerkToSupabaseProfile } from './hooks/useClerkToSupabaseProfile';
// import CV from './assets/pdf/cv/Andrei Karcheuski CV.pdf';

// Define the expected API response type
type ApiResponse = {
  response?: string;
  error?: string;
};

type PageRoute = 'home' | 'accent-detector' | 'video-generator';

const getPageFromPath = (pathname: string): PageRoute => {
  if (pathname === '/accent-detector') return 'accent-detector';
  if (pathname === '/video-generation') return 'video-generator';
  return 'home';
};

const getPathForPage = (page: PageRoute) => {
  if (page === 'accent-detector') return '/accent-detector';
  if (page === 'video-generator') return '/video-generation';
  return '/';
};

function App() {
  useClerkToSupabaseProfile();

  const [currentPage, setCurrentPage] = useState<PageRoute>(() => getPageFromPath(window.location.pathname));
  const [showSecond, setShowSecond] = useState(false);
  const [showThird, setShowThird] = useState(false);
  const [showUnderline, setShowUnderline] = useState(false);
  const [textWidth, setTextWidth] = useState(0);
  const [bolden, setBolden] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const [carouselHeight, setCarouselHeight] = useState(765);
  const strokeWidth = 8;
  const svgPadding = strokeWidth; // 8px on each side
  const [isSmallScreen] = useState<boolean>(window.innerWidth < 768);
  const [isMediumScreen] = useState<boolean>(window.innerWidth < 1025);
  const [contactOpen, setContactOpen] = useState(false);

  // Chat assistant state and animation
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<{ from: 'user' | 'bot'; text: string; timestamp: string }[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [buttonAnimatingOut, setButtonAnimatingOut] = useState(false);
  const [buttonHidden, setButtonHidden] = useState(false);
  const [popupAnimating, setPopupAnimating] = useState(false);
  const [popupClosing, setPopupClosing] = useState(false);
  const VERSION_URL = '/version.json';

  // Chat row input state
  const [chatRowInput, setChatRowInput] = useState("");

  // Handler for sending message from chat row
  const handleChatRowSend = useCallback((msg: string) => {
    if (!msg.trim()) return;
    sendChatMessage(msg);
    setChatRowInput("");
    // Optionally open chat popup if not already open
    if (!chatOpen) setChatOpen(true);
  }, [chatOpen]);

  // Handler for predefined question from chat row
  const handleChatRowPredefined = useCallback((msg: string) => {
    sendChatMessage(msg);
    setChatRowInput("");
    if (!chatOpen) setChatOpen(true);
  }, [chatOpen]);

  // Utility to get API endpoint based on environment
  const getApiUrl = () => {
    // if (process.env.NODE_ENV === 'development') {
    //   return '/api/chat';
    // } else {
    // Production: use deployed API endpoint - Render url
    //return 'https://astrostar-api.onrender.com/chat';
    return 'https://chat.andrei-2f6.workers.dev';
    // }
  };

  // Send message to backend
  const getCurrentTimeHHMM = useCallback(() => {
    const now = new Date();
    return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  }, []);

  const navigateToPage = useCallback((page: PageRoute) => {
    setCurrentPage(page);
    const nextPath = getPathForPage(page);
    if (window.location.pathname !== nextPath) {
      window.history.pushState({}, '', nextPath);
    }
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPage(getPageFromPath(window.location.pathname));
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const sendChatMessage = async (msg: string) => {
    const userMsg = { from: 'user' as const, text: msg, timestamp: getCurrentTimeHHMM() };
    setChatMessages((prev) => [...prev, userMsg]);
    setChatLoading(true);
    try {
      const pattern = /^(connect.*(representative|sales|human|expert|someone))/i;
      if (pattern.test(msg)) return;
      const res = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg })
      });
      let data: ApiResponse | null = null;
      try {
        data = await res.json();
      } catch (jsonErr) {
        const text = await res.text();
        console.error('Non-JSON response:', text);
        setChatMessages((prev) => [...prev, { from: 'bot', text: `Error: ${text}`, timestamp: getCurrentTimeHHMM() }]);
        setChatLoading(false);
        return;
      }
      if (!res.ok) {
        console.error('API error:', res.status, data);
        const errorText = data?.error ?? String(res.status);
        setChatMessages((prev) => [...prev, { from: 'bot', text: `Error: ${errorText}`, timestamp: getCurrentTimeHHMM() }]);
      } else if (data?.response) {
        const responseText = data.response;
        setChatMessages((prev) => [...prev, { from: 'bot', text: responseText, timestamp: getCurrentTimeHHMM() }]);
      } else if (data?.error) {
        const errorText = data.error ?? 'Unknown error';
        setChatMessages((prev) => [...prev, { from: 'bot', text: `Error: ${errorText}`, timestamp: getCurrentTimeHHMM() }]);
      } else {
        setChatMessages((prev) => [...prev, { from: 'bot', text: 'Sorry, there was an error. Please try again.', timestamp: getCurrentTimeHHMM() }]);
      }
    } catch (e: any) {
      console.error('sendChatMessage error:', e);
      setChatMessages((prev) => [...prev, { from: 'bot', text: `Sorry, there was an error. ${e && e.message ? e.message : ''}`, timestamp: getCurrentTimeHHMM() }]);
    }
    setChatLoading(false);
  };

  // Handle predefined question click
  const handlePredefined = (msg) => {
    sendChatMessage(msg);
  };

  // Animation handlers
  const handleOpenChat = () => {
    setButtonAnimatingOut(true);
    setTimeout(() => {
      setButtonHidden(true);
      setChatOpen(true);
      setPopupAnimating(true);
      setTimeout(() => setPopupAnimating(false), 700); // match CSS animation duration
    }, 500); // match button slide out duration
  };

  const handleCloseChat = () => {
    setPopupClosing(true);
    setTimeout(() => {
      setChatOpen(false);
      setPopupClosing(false);
      setButtonHidden(false);
      setButtonAnimatingOut(false);
    }, 700); // match popup slide down duration
  };

  // useEffect(() => {
  //   // handle resize and enforce fixed height on small screens
  //   const updateSizes = () => {
  //     // on large screens, measure the image height if available
  //     const img = document.querySelector('.csl-background .carousel .slide img') as HTMLImageElement | null;
  //     const measured = img?.clientHeight;
  //     setCarouselHeight(measured && measured > 0 ? measured : 765);
  //   };

  //   updateSizes();
  //   window.addEventListener('resize', updateSizes);
  //   // also update after images load (in case image loads later)
  //   const imgs = Array.from(document.querySelectorAll('.csl-background .carousel .slide img')) as HTMLImageElement[];
  //   imgs.forEach(i => i.addEventListener('load', updateSizes));

  //   return () => {
  //     window.removeEventListener('resize', updateSizes);
  //     imgs.forEach(i => i.removeEventListener('load', updateSizes));
  //   };
  // }, []);

  // Dynamically set carousel height based on first image
  // useEffect(() => {
  //   const img = document.querySelector('.csl-background .carousel .slide img');
  //   if (img instanceof HTMLImageElement) {
  //     const updateHeight = () => setCarouselHeight(img.clientHeight || 765);
  //     img.onload = updateHeight;
  //     updateHeight();
  //   }
  // }, []);

  // Auto-open ChatPopup after 10s on large screens, only once per session
  useEffect(() => {
    if (window.innerWidth > 768 && !chatOpen && !sessionStorage.getItem('chatPopupShown')) {
      const timer = setTimeout(() => {
        setChatOpen(true);
        sessionStorage.setItem('chatPopupShown', 'true');
      }, 20000); // 10 seconds
      return () => clearTimeout(timer);
    }
  }, [chatOpen]);

  useEffect(() => {
    const boldenTimer = setTimeout(() => setBolden(true), 100);  // Start boldening after 1s
    // After boldening is complete (e.g. 2s duration), show underline and measure width
    const underlineTimer = setTimeout(() => {
      if (textRef.current) setTextWidth(textRef.current.offsetWidth);
      setShowUnderline(true);
    }, 500); // boldening duration
    const secondTimer = setTimeout(() => setShowSecond(true), 500);
    const thirdTimer = setTimeout(() => setShowThird(true), 1000);
    return () => {
      clearTimeout(boldenTimer);
      clearTimeout(underlineTimer);
      clearTimeout(secondTimer);
      clearTimeout(thirdTimer);
    };
  }, []);

  // Memoized style for header actions
  const headerActionsStyle = useMemo<React.CSSProperties>(() => ({
    gap: isSmallScreen ? '8px' : '16px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: isSmallScreen ? '2px' : '12px',
  }), [isSmallScreen]);

  const accentDetectorBackButtonClassName = isSmallScreen ? 'accent-detector-back-button accent-detector-back-button-mobile' : 'accent-detector-back-button';

  return (
    <div className="App">
      {/* If on accent detector page, show that instead */}
      {currentPage === 'accent-detector' ? (
        <AppLayout
          onNavigate={navigateToPage}
          isSmallScreen={isSmallScreen}
          contactOpen={contactOpen}
          setContactOpen={setContactOpen}
          showFooter={false}
        >
          <AccentDetector />
          <button
            type="button"
            className={accentDetectorBackButtonClassName}
            onClick={() => navigateToPage('home')}
          >
            ← To The Main Page
          </button>
        </AppLayout>
      ) : currentPage === 'video-generator' ? (
        <AppLayout
          onNavigate={navigateToPage}
          isSmallScreen={isSmallScreen}
          contactOpen={contactOpen}
          setContactOpen={setContactOpen}
          showFooter={true}
          style={{ paddingTop: isSmallScreen ? '2.5rem' : '2rem' }}
        >
          <VideoGenerationPage onNavigateHome={() => navigateToPage('home')} />
        </AppLayout>
      ) : (
        /* Main home page */
        <AppLayout
          onNavigate={navigateToPage}
          isSmallScreen={isSmallScreen}
          contactOpen={contactOpen}
          setContactOpen={setContactOpen}
          showFooter={true}
        >
          {/* Floating Chat Button (bottom right, animated) */}
          <FloatingChatButton
            onClick={handleOpenChat}
            animatingOut={buttonAnimatingOut}
            hidden={buttonHidden}
          // animationDurationMs={20500}
          />
          {/* Chat Popup (center bottom, animated) */}
          <ChatPopup
            open={chatOpen}
            onClose={handleCloseChat}
            onSend={sendChatMessage}
            messages={chatMessages}
            onPredefined={handlePredefined}
            animating={popupAnimating}
            closing={popupClosing}
            loading={chatLoading}
            onContactInfo={async ({ email, phone }) => {
              // Send to Formspree endpoint (same as ContactDrawer)
              const FORMSPREE_ENDPOINT = "https://formspree.io/f/xrbonjjw";
              const payload = {
                email,
                phone: phone || '',
                source: 'chat-assistant',
                message: 'User requested to connect with sales via chat assistant.'
              };
              try {
                const res = await fetch(FORMSPREE_ENDPOINT, {
                  method: "POST",
                  headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json"
                  },
                  body: JSON.stringify(payload),
                });
                if (res.ok) {
                  console.log('Contact info sent to Formspree:', payload);
                } else {
                  const err = await res.json().catch(() => null);
                  console.error('Formspree error:', err?.error || res.statusText);
                }
              } catch (e) {
                console.error('Network error sending to Formspree:', e);
              }
            }}
          />
          {/* <Nbr /> */}
          <div className={`carousel-bg-container${isSmallScreen ? ' carousel-bg-container-mobile' : ''}`} style={{ height: `${carouselHeight}px` }}>
            <Csl Photos={Header_H} LowResPhotos={Header_L} Class="csl-background" />

            {/* Centered AnimatedDownArrow */}
            {showThird && (
              <div className="centered-arrow">
                <AnimatedDownArrow />
              </div>
            )}

            <div className="header-overlay">
              <div className="Header">
                <div className="header-content">
                  {showSecond && (
                    <div className="text-default text-header-white text-left fade-in">
                      <RollingText underlineAnimDurationMs={isSmallScreen ? 2000 : 3000} />
                      <div
                        className="header-actions"
                        style={headerActionsStyle}>
                        <ButtonWithArrow
                          text="Get Started"
                          buttonColor="#0078d4"
                          textColor="#fff"
                          hoverTextColor="#fff"
                          hoverButtonColor="#015392"
                          isPrimaryAction
                          fullWidthOnMobile={isSmallScreen ? true : false}
                          style={{
                            position: 'relative',
                            top: isMediumScreen ? -120 : 30,
                            zIndex: 2000,
                            maxWidth: 158
                          }}
                          isArrowAnimation={false}
                          fontSize={` ${isSmallScreen ? '1.1rem' : '1.2rem'}`}
                          onClick={() => setContactOpen(true)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="contentDivider" aria-hidden="true" />

          <AssistantChatRow
            input={chatRowInput}
            setInput={setChatRowInput}
            onSend={handleChatRowSend}
            onPredefined={handleChatRowPredefined}
            isSmallScreen={isSmallScreen}
          />

          <ResponsiveElements isAnimateOnScroll={isSmallScreen}>
            <div className={`tech-row-wrapper${isSmallScreen ? ' tech-row-wrapper-mobile' : ''}`}>
              <div className="tech-row-label"
                style={{
                  fontSize: isSmallScreen ? '1.5rem' : '2rem'
                }}>
                Effective technologies
              </div>
              <TechRow />
            </div>
          </ResponsiveElements>
          {/* Animated Multiline Text Effect (scroll-based) with header */}
          <div className="typing-effect-section">
            <div className="typing-effect-header-row">
              <TypingEffectTextWrapper
                text="AstroStar has the missing solutions you need. How do they work?"
                speed={20}
                horizontalAlign={isSmallScreen ? 'left' : 'center'}
                style={{
                  fontSize: isSmallScreen ? '1.5rem' : '2rem',
                  marginBottom: isSmallScreen ? 50 : 50,
                  marginLeft: 0,
                  maxWidth: 1550,
                  whiteSpace: 'pre-line',
                  overflow: 'visible',
                  display: 'block',
                  color: '#032d60'
                }}
                className="typing-effect-header-centered"
              />
            </div>
            <div className="animated-scroll-text-row">
              <AnimatedScrollMultilineText
                lines={[
                  'AI-powered applications development',
                  'AI agents that work autonomously for you in real-time',
                  'Can be tailored for any business industry',
                  'Intelligent and fast they help you to achieve more'
                ]}
                colorFrom="#4e4e4e"
                colorTo="#000000"
                lineHeightFrom={1.3}
                lineHeightTo={1.1}
                fontSize={isSmallScreen ? '1.2rem' : '1.5rem'}
                fontWeight={isSmallScreen ? 400 : 500}
                slideDistance={isSmallScreen ? 50 : 60}
                horizontalAlign={isSmallScreen ? 'left' : 'center'}
              />
            </div>
          </div>
          <div className="info-section-block">
            <div className='row'>
              <ResponsiveElements isAnimateOnScroll={true} animationOffset={isSmallScreen ? 100 : 200} className='col'>
                <div className='col text-center-block'>
                  <BlockHeader>
                    <div className={`text-default text-header ${isSmallScreen ? ' text-header-small' : 'text-header-large'}`}>
                      Meet Andrei, Astrostar Founder
                    </div>
                  </BlockHeader>
                  <Txb>
                    <div className={`text-default text-content text-left ${isSmallScreen ? ' text-content-sm' : 'text-content-lg'}`}>
                      Software Engineer with over 14 years of international experience, worked in Estonia, Poland and United Arab Emirates.
                      With proven track record of developing enterprise level platforms and focus on building AI-Powered applications
                    </div>
                  </Txb>
                  <VideoPlayer
                    className="fixed-player"
                    src={MainVideo}
                    previewImg={PreviewImg}
                    loop={true}
                    showPreview={true}
                    showProgress={true}
                    darkenOnScroll={false}
                    style={{
                      width: "100%",
                      maxWidth: 490,
                      height: "auto",
                      maxHeight: 580,
                      aspectRatio: "4/5",
                      margin: "5px auto"
                    }}
                  />
                </div>
              </ResponsiveElements>
              <ResponsiveElements isAnimateOnScroll={true} animationOffset={isSmallScreen ? 100 : 500} className='col'>
                <div className='col'>
                  <BlockHeader>
                    <div className={`text-default text-header ${isSmallScreen ? ' text-header-small' : 'text-header-large'}`}>
                      Participation on StackOverflow(2011-Present)
                    </div>
                  </BlockHeader>
                  <Txb>
                    <div className={`text-default text-content text-left ${isSmallScreen ? ' text-content-sm' : 'text-content-lg'}`}>
                      Over 14 years of membership and participation on
                      <ButtonWithArrow
                        isLink={true}
                        isArrowAnimation={false}
                        text=" StackOverflow "
                        url="https://stackoverflow.com/users/1015577/andrei-karcheuski"
                        buttonColor="transparent"
                        textColor="#005bb1ff"
                        hoverTextColor="#00284dff"
                        borderColor="#005bb1ff"
                        hoverButtonColor="#fff"
                        fontSize={` ${isSmallScreen ? '0.9rem' : '1.1rem'}`}
                      />
                      website for programmers,
                      earned more than 3000 reputation points for valued contributions such as 23 helpful answers.
                      My top tags are: <b>SQL Server</b>, <b>SQL</b>, <b>Git</b> and <b>C#</b>. <br />
                      Here is the
                      <ButtonWithArrow
                        isLink={true}
                        text="link to my StackOverflow profile"
                        url="https://stackoverflow.com/users/1015577/andrei-karcheuski"
                        buttonColor="transparent"
                        textColor="#005bb1ff"
                        hoverTextColor="#00284dff"
                        hoverButtonColor="#fff"
                        fontSize={` ${isSmallScreen ? '0.9rem' : '1.1rem'}`}
                      /> <br />
                      Also visit my
                      <ButtonWithArrow
                        isLink={true}
                        // isAnimated={!isSmallScreen}
                        text=" LinkedIn profile "
                        url="https://www.linkedin.com/in/karcheuski"
                        buttonColor="transparent"
                        textColor="#005bb1ff"
                        hoverTextColor="#00284dff"
                        hoverButtonColor="#fff"
                        fontSize={` ${isSmallScreen ? '0.9rem' : '1.1rem'}`}
                      />
                      to see recommendations from colleagues and supervisors.
                    </div>
                  </Txb>
                  <AnimatedHoopWithImage lowSrc={SoImg3_L} highSrc={SoImg3_H} stroke1="#00b1a2ff" stroke2="#00ffdd" />
                </div>
              </ResponsiveElements>
            </div>
          </div>
          <div className='row'>
            <ResponsiveElements isAnimateOnScroll={true} animationOffset={isSmallScreen ? 100 : 200} className='col'>
              <div className='col'>
                <>
                  <BlockHeader>
                    <div className={`text-default text-header ${isSmallScreen ? ' text-header-small' : 'text-header-large'}`}>
                      My work in Estonia (2017 - 2021)
                    </div>
                  </BlockHeader>
                  <Txb>
                    <div className={`text-default text-content text-left ${isSmallScreen ? ' text-content-sm' : 'text-content-lg'}`}>
                      In 2017 I received <b>job offer with relocation to Estonia</b>, which is globally recognized as <b>one of the most digitally advanced countries</b>, with a robust and innovative Information Technology sector.
                      <p><b>I spent 4 years in Estonia working as Software Engineer</b> at <b>global technology solutions company HireRight</b>, leading provider of on-demand employment background checks, drug testing and education verifications in highly skilled geographically distributed team (UK and Estonia) developing <b>world-class software platforms</b>, which gave me valuable professional experience.</p>
                      <p>Here are my Estonian id-card until 08.03.2023 and
                        <ButtonWithArrow
                          isLink={true}
                          text="Employment Confirmation letter"
                          url={HireRight}
                          buttonColor="transparent"
                          textColor="#005bb1ff"
                          hoverTextColor="#00284dff"
                          hoverButtonColor="#fff"
                          fontSize={` ${isSmallScreen ? '0.9rem' : '1.1rem'}`}
                        /></p>
                    </div>
                  </Txb>
                </>
                <Csl Photos={Estonia_H} LowResPhotos={Estonia_L} Class='csl' />
              </div>
            </ResponsiveElements>
            <ResponsiveElements isAnimateOnScroll={true} animationOffset={isSmallScreen ? 100 : 500} className='col'>
              <div className='col'>
                <>
                  <BlockHeader>
                    <div className={`text-default text-header ${isSmallScreen ? ' text-header-small' : 'text-header-large'}`}>
                      My work in Poland
                    </div>
                  </BlockHeader>
                  <Txb>
                    <div className={`text-default text-content text-left ${isSmallScreen ? ' text-content-sm' : 'text-content-lg'}`}>
                      After successfully passing the hiring process In January 2022 I have received job offer for the position of <b>Senior Software Developer</b> at <b>Luxoft, digital strategy and software engineering firm,</b> with <b>relocation to Poland </b>
                      which has ones of most talented programmers in the world accordingly HackerRank data.
                      <p>It was the great opportunity to continue my already established international career by joining another successful company.</p>
                      <p>Here are my Poland Business Harbor visa issued in Tallinn and
                        <ButtonWithArrow
                          isLink={true}
                          text="Certificate of Employment"
                          url={Luxoft}
                          buttonColor="transparent"
                          textColor="#005bb1ff"
                          hoverTextColor="#00284dff"
                          hoverButtonColor="#fff"
                          fontSize={` ${isSmallScreen ? '0.9rem' : '1.1rem'}`}
                        /></p>
                    </div>
                  </Txb>
                </>
                <Csl Photos={Poland_H} LowResPhotos={Poland_L} Class='csl' />
              </div>
            </ResponsiveElements>
          </div>

          <div className=' row'>
            <ResponsiveElements isAnimateOnScroll={true} animationOffset={isSmallScreen ? 100 : 200} className='col'>
              <div className='col'>
                <>
                  <BlockHeader>
                    <div className={`text-default text-header ${isSmallScreen ? ' text-header-small' : 'text-header-large'}`}>
                      Work at Seavus (2015 - 2016)
                    </div>
                  </BlockHeader>
                  <Txb>
                    <div className={`text-default text-content text-left ${isSmallScreen ? ' text-content-sm' : 'text-content-lg'}`}>
                      Here is another remarkable milestone in my career - work at software development company Seavus,
                      where I was developing innovative platform for managing automotive parts purchases Global InPart,
                      using effective technologies such as RabbitMQ message broker.
                      Here is my
                      <ButtonWithArrow
                        isLink={true}
                        text="Seavus Employment Certificate"
                        url={Luxoft}
                        buttonColor="transparent"
                        textColor="#005bb1ff"
                        hoverTextColor="#00284dff"
                        hoverButtonColor="#fff"
                        fontSize={` ${isSmallScreen ? '0.9rem' : '1.1rem'}`}
                      /></div>
                  </Txb>
                  <ProgressiveImage lowSrc={SeavusImg_L} highSrc={SeavusImg_H} className="responsive-img magnification" />
                  {/* <img
              src={SeavusImg_H}
              alt="Seavus"
              className="responsive-img"
              loading="lazy"
            /> */}
                </>
              </div>
            </ResponsiveElements>
            <ResponsiveElements isAnimateOnScroll={true} animationOffset={isSmallScreen ? 100 : 500} className='col'>
              <div className='col'>
                <>
                  <BlockHeader>
                    <div className={`text-default text-header ${isSmallScreen ? ' text-header-small' : 'text-header-large'}`}>
                      English Score Certificate
                    </div>
                  </BlockHeader>
                  <Txb>
                    <div className={`text-default text-content text-left ${isSmallScreen ? ' text-content-sm' : 'text-content-lg'}`}>
                      In Septemeber 2025 I have achieved CEFR C1 in <b>EnglishScore Core Skills test</b>, use code <b>357dcbb4a083</b> at 
                      <ButtonWithArrow
                        isLink={true}
                        isArrowAnimation={false}
                        text=" englishscore.com/verify "
                        url="https://www.englishscore.com/verify"
                        buttonColor="transparent"
                        textColor="#005bb1ff"
                        hoverTextColor="#00284dff"
                        hoverButtonColor="#fff"
                        fontSize={` ${isSmallScreen ? '0.9rem' : '1.1rem'}`}
                      />
                      {/* <ButtonWithArrow
                        isLink={true}
                        text="EF SET English Certificate"
                        url="https://cert.efset.org/en/DGuxfg"
                        buttonColor="transparent"
                        textColor="#005bb1ff"
                        hoverTextColor="#00284dff"
                        hoverButtonColor="#fff"
                        fontSize={` ${isSmallScreen ? '0.9rem' : '1.1rem'}`}
                      /> */}
                    </div>
                  </Txb>
                  <PdfPreview
                    src={EnglishScore}
                    title="English Score Certificate first page"
                    height={` ${isSmallScreen ? '550px' : '860px'}`}
                    style={{ marginTop: '1rem', boxSizing: 'border-box' }}
                  />
                </>
              </div>
            </ResponsiveElements>
          </div>

          <div className=' row'>
            <ResponsiveElements isAnimateOnScroll={true} animationOffset={isSmallScreen ? 100 : 200} className='col'>
              <div className='col'>
                <>
                  <BlockHeader>
                    <div className={`text-default text-header ${isSmallScreen ? ' text-header-small' : 'text-header-large'}`}>
                      France (2022 - 2023)
                    </div>
                  </BlockHeader>
                  <Txb>
                    <div className={`text-default text-content text-left ${isSmallScreen ? ' text-content-sm' : 'text-content-lg'}`}>
                      <p><b>Paris:</b> Eiffel Tower, Louvre, Tuileries Garden</p>
                      <p><b>Marseille:</b> Old Port, Porte d'Aix</p>
                      <p><b>Lyon:</b> Pont de la Feuillée, Parc de la Tete d'Or</p>
                      <p><b>Nice:</b> Promenade des Anglais, Fontaine du Soleil</p>
                    </div>
                  </Txb>
                  <Csl Photos={France_H} LowResPhotos={France_L} Class='csl' />
                </>
              </div>
            </ResponsiveElements>
            <ResponsiveElements isAnimateOnScroll={true} animationOffset={isSmallScreen ? 100 : 500} className='col'>
              <div className='col'>
                <>
                  <BlockHeader>
                    <div className={`text-default text-header ${isSmallScreen ? ' text-header-small' : 'text-header-large'}`}>
                      United Arab Emirates (2023 - 2024)
                    </div>
                  </BlockHeader>
                  <Txb>
                    <div className={`text-default text-content text-left ${isSmallScreen ? ' text-content-sm' : 'text-content-lg'}`}>
                      <p><b>Dubai</b>: Burj Khalifa, Dubai Marina, Kite Beach, Al Mamzer Beach, La Mer Beach</p>
                      <p><b>Abu Dhabi</b>: View from ADGM World's premier International Financial Centre.</p>
                    </div>
                  </Txb>
                  <Csl Photos={UAE_H} LowResPhotos={UAE_L} Class='csl' />
                </>
              </div>
            </ResponsiveElements>
          </div>

          <div className="info-section-block info-section-block-alt">
            <div className=' row'>
              <ResponsiveElements isAnimateOnScroll={true} animationOffset={isSmallScreen ? 100 : 200} className='col'>
                <div className='col'>
                  <>
                    <BlockHeader>
                      <div className={`text-default text-header ${isSmallScreen ? ' text-header-small' : 'text-header-large'}`}>
                        Kite Surfing
                      </div>
                    </BlockHeader>
                    <Txb>
                      <div className={`text-default text-content text-left ${isSmallScreen ? ' text-content-sm' : 'text-content-lg'}`}>
                        Kite Surfing is great sport and leisure activity.
                        It combines the thrill of surfing with the excitement of flying a kite,
                        allowing to harness the power of the wind to glide across the water.
                        I enjoy the sense of freedom and adventure that comes with kite surfing.
                        Here is a video of me kite surfing in Dubai.
                      </div>
                    </Txb>
                    <VideoPlayer
                      src={KiteVideo}
                      autoplay={false}
                      controls={true}
                      muted={false}
                      showPreview={false}
                      showProgress={false}
                      darkenOnScroll={true}
                      darkenMax={1}
                      fadeRange={700}
                      style={{ maxWidth: 700, height: 'auto', aspectRatio: '16/9', margin: '5px auto' }}
                    />
                  </>
                </div>
              </ResponsiveElements>
              <ResponsiveElements isAnimateOnScroll={true} animationOffset={isSmallScreen ? 100 : 500} className='col'>
                <div className='col'>
                  <>
                    <BlockHeader>
                      <div className={`text-default text-header ${isSmallScreen ? ' text-header-small' : 'text-header-large'}`}>
                        Having great time in Greece
                      </div>
                    </BlockHeader>
                    <Txb>
                      <div className={`text-default text-content text-left ${isSmallScreen ? ' text-content-sm' : 'text-content-lg'}`}>
                        <p>Amazing places in Athens: Roman Agora, Metropolitan Cathedral</p>
                      </div>
                    </Txb>
                    <Csl Photos={Greece_H} LowResPhotos={Greece_L} Class='csl' />
                  </>
                </div>
              </ResponsiveElements>
            </div>
          </div>

          <div className=' row'>
            <ResponsiveElements isAnimateOnScroll={true} animationOffset={isSmallScreen ? 100 : 200} className='col'>
              <div className='col'>
                <>
                  <BlockHeader>
                    <div className={`text-default text-header ${isSmallScreen ? ' text-header-small' : 'text-header-large'}`}>
                      Visiting Spain
                    </div>
                  </BlockHeader>
                  <Txb>
                    <div className={`text-default text-content text-left ${isSmallScreen ? ' text-content-sm' : 'text-content-lg'}`}>
                      Turned out that I'm into Spanish cuisine, here are some of my favorite dishes I used to eat
                      - Tortilla de patatas (omelet made from potatoes, eggs and onions) and Gazpacho (cool drink from tomatoes, bell peppers, garlic, cucumbers and olive oil)
                    </div>
                  </Txb>
                  <Csl Photos={Spain_H} LowResPhotos={Spain_L} />
                </>
              </div>
            </ResponsiveElements>
            <ResponsiveElements isAnimateOnScroll={true} animationOffset={isSmallScreen ? 100 : 500} className='col'>
              <div className='col'>
                <>
                  <BlockHeader>      <div className={`text-default text-header ${isSmallScreen ? ' text-header-small' : 'text-header-large'}`}>
                    Visiting India
                  </div>
                  </BlockHeader>
                  <Txb>
                    <div className={`text-default text-content text-left ${isSmallScreen ? ' text-content-sm' : 'text-content-lg'}`}>
                      Visited India to meet with my former colleagues with whom I was working at UBS as external resource engineer,
                      very liked tropical fruits and local snacks like jackfruit chips
                    </div>
                  </Txb>
                  <Csl Photos={India_H} LowResPhotos={India_L} Class='csl' />
                </>
              </div>
            </ResponsiveElements>
          </div>


        </AppLayout>
      )}
    </div >

  );
}

export default App;
